import { useSetState } from 'ahooks'
import { Form, Picker, Tabs, Badge } from 'antd-mobile'
import classNames from 'classnames'
import { isNil } from 'lodash-es'
import { observer } from 'mobx-react-lite'
import { useContext, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import useFunctionCall from '@/components/NewEditForm/hooks/useFunctionCall'
import PickerBox from '@/components/NewEditForm/UILibrary/HYBElement/PickerBox'
import { HSearchBar } from '@/components/NewEditForm/UILibrary/HYBVant'
import HTabs from '@/components/NewEditForm/UILibrary/HYBVant/HTabs'
import { isLowcodeVersion } from '@/components/NewEditForm/utils'
import { type IPageProps } from '@/components/UnifiedPage/interface'
import { type UnifiedStateStoreType } from '@/hooks'
import { unifiedStore } from '@/store'
import { isNotEmptyArray } from '@/utils'

import { DatePicker, OrgFilter, renderFormItem, RenderNode } from './components'

import './index.less'

/**
 * 通用列表布局组件
 * @returns FC
 */
const Filter = ({
  formInst,
  stateStore: propStateStore,
  dataSource,
  optionStore,
  onValuesChange
}: {
  formInst?: any
  optionStore: IPageProps
  stateStore: UnifiedStateStoreType
  dataSource: {
    runQuery?: Function
    form_list?: any[]
    confirm_form_list?: any[]
    reset_field_name_list?: string[]
  }
  onValuesChange?: (changed: any) => void
}) => {
  const _stateStore = useContext(unifiedStore)
  const stateStore = propStateStore || _stateStore
  const navigate = useNavigate()
  const location = useLocation()
  const [{ isReset }, setState] = useSetState<any>({
    isReset: false
  })
  const { form_list = [], confirm_form_list = [], reset_field_name_list = [] } = dataSource
  const tempForm = Form.useForm()?.[0]
  const form = formInst || tempForm
  const { hideExec, onAfterChangeExec } = useFunctionCall({
    form,
    filterValues: stateStore.filterState?.filterValues || {}
  })
  const isLowCode = isLowcodeVersion(optionStore.filterOption && optionStore.filterOption?.version)

  const resetFieldValues = isNotEmptyArray(reset_field_name_list)
    ? reset_field_name_list.reduce((res: any, key: string) => {
        res[key] = null
        return res
      }, {})
    : {}
  const handleChange = (changed: any) => {
    stateStore.setFilterState({
      filterValues: { ...stateStore.filterState?.filterValues, ...resetFieldValues, ...changed }
    })
    stateStore.setTableState({
      selectedKeys: [],
      selectedItems: []
    })
    onValuesChange?.(changed)
  }
  /**
   * 定制平铺筛选器
   * @param item
   * @returns
   */
  const renderFilterItem = (item: any, index: number) => {
    switch (item.type) {
      case 'Input':
        return (
          <Form.Item name='' label='' key={`Input-${index}`}>
            <HSearchBar
              value={
                stateStore.filterState?.filterValues?.[item?.formItemProps?.name || item?.name]
              }
              onChange={(e: any) => {
                const text = item?.formItemProps?.name || item?.name
                const info: any = {
                  [text]: e
                }
                handleChange(info)
              }}
              placeholder={item?.formItemProps?.placeholder || item?.placeholder}
            />
          </Form.Item>
        )
      case 'Select':
        return (
          <Form.Item name='' label='' key={`Select-${index}`}>
            <PickerBox
              value={
                stateStore.filterState?.filterValues?.[item?.formItemProps?.name || item?.name]
              }
              onConfirm={(e: any) => {
                const text = item?.formItemProps?.name || item?.name
                const info: any = {
                  [text]: e
                }
                handleChange(info)
              }}
              {...item.elementProps}
            />
          </Form.Item>
        )
      case 'OrgFilter':
        return (
          <Form.Item name='' label='' key={`OrgFilter-${index}`}>
            <OrgFilter
              {...item.elementProps}
              value={
                stateStore.filterState?.filterValues?.[item?.formItemProps?.name || item?.name]
              }
              onConfirm={(e: any) => {
                const text = item?.formItemProps?.name || item?.name
                const info: any = {
                  [text]: e
                }
                handleChange(info)
              }}
            />
          </Form.Item>
        )
      case 'DatePicker':
        return (
          <Form.Item name='' label='' key={`DatePicker-${index}`}>
            <DatePicker
              value={
                stateStore.filterState?.filterValues?.[item?.formItemProps?.name || item?.name]
              }
              onConfirm={(e: any) => {
                const text = item?.formItemProps?.name || item?.name
                const info: any = {
                  [text]: e
                }
                handleChange(info)
              }}
              {...item.elementProps}
            />
          </Form.Item>
        )
      case 'Tabs':
        return (
          <HTabs
            key={`Tabs-${index}`}
            activeKey={
              stateStore.filterState?.filterValues?.[item?.formItemProps?.name || item?.name]
            }
            defaultActiveKey={location.state?.[item?.formItemProps?.name || item.name]}
            onChange={(val: any) => {
              navigate(location.pathname + location.search, {
                state: {
                  [item?.formItemProps?.name || item.name]: val
                },
                replace: true
              })
              const changed = {
                [item?.formItemProps?.name || item.name]: val
              }
              if (Array.isArray(item?.elementProps?.clearFields)) {
                item?.elementProps?.clearFields?.reduce((prev: any, next: any) => {
                  prev[next] = undefined
                  return prev
                }, changed)
              }
              let defaultValueRetention = changed
              if (item?.elementProps?.defaultValueRetention) {
                defaultValueRetention = {
                  ...changed,
                  ...item?.elementProps?.defaultValueRetention
                }
              }
              window.scrollTo(0, 0)
              setState({ isReset: true })
              setTimeout(() => {
                setState({ isReset: false })
              }, 100)
              handleChange(defaultValueRetention)
            }}
          >
            {(item.elementProps?.dataList || []).map((tab: any, dataIndex: number) => {
              const show_total = item.elementProps?.showTotal && tab?.showTotal !== false
              const list = stateStore.filterState.tabsTotal || []
              const title = (
                <Badge
                  // content={Number.isInteger(list?.[dataIndex]) ? list?.[dataIndex] : ''}
                  content={show_total ? list?.[dataIndex] : ''}
                  style={{ '--right': '-10px', '--top': '8px' }}
                >
                  {tab.text}
                </Badge>
              )
              return <Tabs.Tab key={tab.value} title={show_total ? title : tab.text} />
            })}
          </HTabs>
        )
    }
    return null
  }

  useEffect(() => {
    const defaultFiles = (
      isNotEmptyArray(form_list) ? form_list.filter((item) => item.required) : []
    ).reduce((res, item) => {
      const { dataList } = item?.elementProps || {}
      const firstValue = isNotEmptyArray(dataList) ? dataList[0].value : undefined
      if (!isNil(firstValue)) {
        res[item.name] = firstValue
      }
      return res
    }, {})
    const tabsFieldName = form_list.find((item) => item.type === 'Tabs')?.name
    const _filterState: any = {
      form,
      filterValues: {
        ...defaultFiles,
        ...optionStore.pageOption?.services?.query?.data
      }
    }
    if (tabsFieldName && !isNil(location.state?.[tabsFieldName])) {
      _filterState.filterValues[tabsFieldName] = location.state?.[tabsFieldName]
    }
    stateStore.setFilterState(_filterState)
  }, [form_list])

  console.log(confirm_form_list, 'confirm_form_list')

  return (
    <div className='unified-filter-wrapper js-unified-filter-wrapper'>
      <Form form={form}>
        <div
          className={classNames('unified-filter  js-unified-filter', {
            hasTabs: form_list?.findIndex((i: any) => i.type === 'Tabs') === 0
          })}
        >
          {form_list?.map(renderFilterItem)}
          {confirm_form_list?.length > 0 && (
            <Form.Item {...renderFormItem('Expand')}>
              <RenderNode
                type='Expand'
                elementProps={{
                  form,
                  stateStore,
                  form_list: confirm_form_list,
                  globalProps: { state: {}, option: {}, hideExec, onAfterChangeExec },
                  isLowCode,
                  isReset
                }}
              />
            </Form.Item>
          )}
        </div>
      </Form>
    </div>
  )
}

export default observer(Filter)
